import {
    CHART_TYPES,
    type ChartData,
    type DateRangeObject,
    type PageRouteData,
    type ProductAnalyticsRow,
} from '../types/analytics.types';
import { PERIOD_LENGTHS, PERIOD_THRESHOLDS, RANGE_TYPES } from '../constants/analytics.constants';

/**
 * Pure logic functions for analytics data processing
 * No API calls - these are handled by query hooks
 */

/**
 * Transforms analytics metrics data into the expected format
 */
export function transformAnalyticsMetrics(data: Record<string, ChartData>) {
    return {
        views: data[CHART_TYPES.VIEWS],
        revenue: data[CHART_TYPES.REVENUE],
        leads: data[CHART_TYPES.LEADS],
    };
}

/**
 * Transforms product analytics data into the expected format
 */
export function transformProductAnalytics(data: Record<string, ChartData>) {
    return {
        [CHART_TYPES.PAGE_CONVERSION]: data[CHART_TYPES.PAGE_CONVERSION],
        [CHART_TYPES.PAGE_TRAFFIC_BREAKDOWN]: data[CHART_TYPES.PAGE_TRAFFIC_BREAKDOWN],
        [CHART_TYPES.REVENUE]: data[CHART_TYPES.REVENUE],
        [CHART_TYPES.ORDERS]: data[CHART_TYPES.ORDERS],
    };
}

/**
 * Calculates the day range from a DateRangeObject.
 * Returns the number of days between start and end dates.
 * For custom ranges, adds 1 day to include the end date.
 * Returns 14 days as default if dates are not provided.
 */
export function calculateAnalyticsRange(dateRange: DateRangeObject): number {
    const { startDate, endDate, rangeType } = dateRange;

    // If only one date is provided, return 0 (invalid)
    if ((startDate && !endDate) || (!startDate && endDate)) return 0;

    // Default to 14 days if no dates provided
    if (!startDate || !endDate) return 14;

    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    // For custom and monthToDate ranges, add 1 to include the end date
    return diffDays + (rangeType === RANGE_TYPES.CUSTOM || rangeType === RANGE_TYPES.MONTH_TO_DATE ? 1 : 0);
}

/**
 * Calculates the period length for analytics data aggregation based on the day range.
 * Uses progressive aggregation to optimize chart readability and performance:
 * - 0-30 days: daily aggregation (1 day per data point)
 * - 31-60 days: bi-daily aggregation (2 days per data point)
 * - 61-90 days: weekly aggregation (7 days per data point)
 * - 90+ days: bi-weekly aggregation (14 days per data point)
 */
export function calculatePeriodLength(range: number): number {
    if (range <= PERIOD_THRESHOLDS.DAILY_MAX) return PERIOD_LENGTHS.DAILY;
    if (range <= PERIOD_THRESHOLDS.BI_DAILY_MAX) return PERIOD_LENGTHS.BI_DAILY;
    if (range <= PERIOD_THRESHOLDS.WEEKLY_MAX) return PERIOD_LENGTHS.WEEKLY;
    return PERIOD_LENGTHS.BI_WEEKLY;
}

/**
 * Converts the end date to a Unix timestamp (in seconds) for API requests.
 * Returns 0 if the date is undefined.
 */
export function getEndDateTimestamp(endDate: Date | undefined): number {
    return endDate ? Math.round(endDate.getTime() / 1000) : 0;
}

/**
 * Transforms API chart values into an array format suitable for charting.
 * For arrays, returns as-is. For objects, sums values at each index position.
 */
export function transformChartValues(values: Record<string, number[]> | number[]): number[] {
    if (Array.isArray(values)) {
        return values;
    }

    // For object values (like stacked revenue), sum all values at each index
    const arrays = Object.values(values);
    if (arrays.length === 0) return [];

    const maxLength = Math.max(...arrays.map(arr => arr.length));
    const result = new Array(maxLength).fill(0);

    arrays.forEach(arr => {
        arr.forEach((value, index) => {
            if (index < result.length) {
                result[index] += value;
            }
        });
    });

    return result;
}

/**
 * Transforms product analytics data into product rows with combined metrics.
 * Combines data from multiple chart types (views, orders, conversion, revenue)
 * into a single row per product, sorted by revenue descending.
 */
export function transformProductAnalyticsData(transformedData: Record<string, ChartData | undefined>): {
    products: ProductAnalyticsRow[];
    pagesRouteData: Record<string, PageRouteData>;
} {
    // Extract pages route data from any response that has it
    let routeData: Record<string, PageRouteData> = {};
    Object.values(transformedData).forEach((chartData: ChartData | undefined) => {
        if (chartData?.pages_route_data) {
            routeData = chartData.pages_route_data as Record<string, PageRouteData>;
        }
    });

    /**
     * Transform API responses into product analytics rows.
     * Combines data from multiple chart types (views, orders, conversion, revenue)
     * into a single row per product, sorted by revenue descending.
     */
    const productMap: Record<string, ProductAnalyticsRow> = {};

    // Process each chart type's data
    Object.entries(transformedData).forEach(([chartType, chartData]) => {
        if (!chartData?.totals) return;

        Object.entries(chartData.totals).forEach(([productName, value]) => {
            if (!productMap[productName]) {
                productMap[productName] = {
                    product: productName,
                    views: 0,
                    orders: 0,
                    conversion: 0,
                    revenue: 0,
                };
            }

            switch (chartType) {
                case CHART_TYPES.PAGE_TRAFFIC_BREAKDOWN:
                    productMap[productName].views = value as number;
                    break;
                case CHART_TYPES.ORDERS:
                    productMap[productName].orders = value as number;
                    break;
                case CHART_TYPES.PAGE_CONVERSION:
                    productMap[productName].conversion = value as number;
                    break;
                case CHART_TYPES.REVENUE:
                    productMap[productName].revenue = value as number;
                    break;
            }
        });
    });

    const productsArray = Object.values(productMap);

    // Sort by revenue descending
    productsArray.sort((a, b) => b.revenue - a.revenue);

    return { products: productsArray, pagesRouteData: routeData };
}
